import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import WbPageEditor from '../whistleblowing/WbPageEditor';
import { KitPanel } from './KitPage';
import { Megaphone, Mail, Phone, Loader2, CheckCircle2, ShieldCheck, Lock, FileText, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { api, errMsg } from '../api';
import { PageHeader, Card, goldBtn, ReadMore } from '../ui';
import { usePortal } from '../PortalAuth';
import WbManager from '../whistleblowing/WbManager';
import Infographic from '../legal/Infographic';

const DOC = '/docs/BOND_Whistleblowing_PMI_Obblighi_di_legge.pdf';
const BG = [[Megaphone, 'Canale di segnalazione', 'Segnalazioni scritte e orali, anche in forma anonima'], [Lock, 'Riservatezza', 'Identità del segnalante protetta e accessi tracciati'],
  [FileText, 'Gestione dei casi', 'Ricevute, scadenze e registro delle attività'], [ShieldCheck, 'Conformità', 'Procedure e informativa per i dipendenti']];

function Background() {
  return (
    <div className="pointer-events-none select-none blur-[3px] opacity-60" aria-hidden="true">
      <PageHeader kicker="Segnalazioni" title="Whistleblowing" />
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        {BG.map(([Icon, t, d]) => <Card key={t} className="p-6"><Icon size={20} className="text-[#D4AF37] mb-3" /><p className="text-base text-[#F2F2F2] mb-1">{t}</p><p className="text-sm text-[#8A8F97]">{d}</p></Card>)}
      </div>
      <Card className="p-6 h-72"><p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-4">Segnalazioni recenti</p>{[1, 2, 3, 4].map((i) => <div key={i} className="h-10 border-t border-[#1F242C]" />)}</Card>
    </div>
  );
}

function Locked() {
  const [state, setState] = useState('idle');
  const request = async () => { setState('busy'); try { await api.post('/whistleblowing/request-access'); setState('sent'); } catch (e) { toast.error(errMsg(e)); setState('idle'); } };
  return (
    <div className="relative" data-testid="whistleblowing-locked">
      <Background />
      <Dialog open modal={false}>
        <DialogContent className="design-dialog max-w-3xl max-h-[92vh] overflow-y-auto [&>button]:hidden" data-testid="whistleblowing-dialog" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <div className="w-11 h-11 rounded-full grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37]"><Megaphone size={20} /></div>
          <div><Infographic kind="whistleblowing" full /></div>
          <DialogTitle className="font-heading text-2xl leading-tight">Whistleblowing: un obbligo di legge, con sanzioni fino a 50.000 €</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm opacity-85 -mt-1 leading-relaxed" data-testid="whistleblowing-text"><ReadMore clamp="max-sm:line-clamp-6" testid="whistleblowing-text-body"><div className="space-y-3">
              <p>Il D.Lgs. 24/2023 obbliga le imprese con almeno 50 dipendenti, e quelle con Modello 231 di qualsiasi dimensione, ad attivare un canale riservato per le segnalazioni di illeciti. Il canale va affidato a un gestore autonomo e formato e deve rispettare i tempi di legge: avviso di ricevimento entro 7 giorni, riscontro entro 3 mesi.</p>
              <div>
                <p className="font-semibold">Sanzioni ANAC (art. 21):</p>
                <ul className="list-disc pl-5 space-y-1 mt-1">
                  <li>da 10.000 a 50.000 € se il canale non è istituito, se la procedura non è conforme o se le segnalazioni non vengono verificate;</li>
                  <li>da 10.000 a 50.000 € per ritorsioni contro il segnalante, ostacolo alla segnalazione o violazione della riservatezza.</li>
                </ul>
              </div>
              <p><strong>Attenzione anche al D.Lgs. 231/2001:</strong> se il canale di segnalazione non è conforme, il Modello 231 non protegge più l'azienda dalla responsabilità per i reati commessi al suo interno.</p>
              <p className="font-semibold">Affida a Smart Info sia la gestione del canale, come soggetto terzo autonomo, sia gli accertamenti sui fatti segnalati, come agenzia investigativa autorizzata.</p>
              <p className="text-xs opacity-80">Il servizio Whistleblowing non è ancora attivo sul tuo account: contatta Smart Info per farti abilitare.</p>
            </div></ReadMore></div>
          </DialogDescription>
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <a href="mailto:info@smart-info.it" className="flex items-center gap-2 px-3 py-2.5 border border-[#1F242C] rounded-sm hover:border-[#D4AF37]" data-testid="whistleblowing-contact-email"><Mail size={15} className="text-[#D4AF37]" />info@smart-info.it</a>
            <a href="tel:800778055" className="flex items-center gap-2 px-3 py-2.5 border border-[#1F242C] rounded-sm hover:border-[#D4AF37]" data-testid="whistleblowing-contact-phone"><Phone size={15} className="text-[#D4AF37]" />Numero Verde 800 778 055</a>
          </div>
          <a href={DOC} download className="flex items-center gap-2 text-sm text-[#D4AF37] hover:underline" data-testid="whistleblowing-doc-link"><FileDown size={15} />Scarica l'approfondimento: obblighi di legge, gestione e indagini (PDF)</a>
          {state === 'sent'
            ? <p className="flex items-center gap-2 text-sm text-[#5FC18F]" data-testid="whistleblowing-request-sent"><CheckCircle2 size={16} />Richiesta inviata: Smart Info ti contatterà a breve.</p>
            : <div><button onClick={request} disabled={state === 'busy'} className={`${goldBtn} h-11 px-6 text-sm inline-flex items-center gap-2`} data-testid="whistleblowing-request-btn">{state === 'busy' && <Loader2 size={15} className="animate-spin" />}Richiedi abilitazione</button></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TABS = [['canale', 'Canale e segnalazioni', 'Canale'], ['pagina', 'Pagina pubblica', 'Pagina'], ['kit', 'Kit documenti', 'Kit']];
const tabCls = 'rounded-none px-4 py-3 text-sm text-[#8A8F97] focus-visible:ring-0 focus-visible:ring-offset-0 outline-none data-[state=active]:bg-transparent data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] whitespace-nowrap';

function Enabled({ user }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const reload = () => api.get('/whistleblowing/channel').then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { reload(); }, []);
  const tab = TABS.some(([v]) => v === params.get('tab')) ? params.get('tab') : 'canale';
  if (!data) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  const active = data.channel?.active;
  return (
    <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
      <TabsList className="bg-transparent border-b border-[#1F242C] rounded-none w-full justify-start h-auto p-0 mb-6 overflow-x-auto flex-nowrap">
        {TABS.map(([v, l, s]) => <TabsTrigger key={v} value={v} className={tabCls} data-testid={`wb-tab-${v}`}><span className="hidden sm:inline">{l}</span><span className="sm:hidden">{s}</span></TabsTrigger>)}
      </TabsList>
      <TabsContent value="canale"><WbManager user={user} data={data} reload={reload} /></TabsContent>
      <TabsContent value="pagina">{active ? <WbPageEditor key={data.channel.company + data.channel.intro} data={data} isOwner={user.is_owner !== false} reload={reload} />
        : <Card className="p-6 text-sm text-[#8A8F97]" data-testid="wb-page-inactive">Attiva prima il canale dalla scheda «Canale e segnalazioni»: poi potrai personalizzare qui la pagina pubblica.</Card>}</TabsContent>
      <TabsContent value="kit"><KitPanel /></TabsContent>
    </Tabs>
  );
}

export default function WhistleblowingPage() {
  const { user } = usePortal();
  if (!user.whistleblowing_enabled) return <Locked />;
  return (
    <div data-testid="whistleblowing-page">
      <PageHeader kicker="Segnalazioni" title="Whistleblowing">
        <a href={DOC} download className="h-10 px-4 flex items-center gap-2 text-sm border border-[#1F242C] rounded-sm text-[#A3A3A3] hover:text-[#D4AF37]" data-testid="whistleblowing-doc-link-page"><FileDown size={15} />Obblighi di legge (PDF)</a>
      </PageHeader>
      <Enabled user={user} />
    </div>
  );
}
